import React, { useCallback, useEffect, useMemo, useState } from 'react';
import io from 'socket.io-client';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import { getToken } from '../services/authorize';
import ThaiDateInput from '../components/ThaiDateInput'
import { toISODate } from '../utils/thaiDate'

const todayISO = () => toISODate(new Date());

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
const StudentAvatar = ({ photo, name }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const circleStyle = { width: 28, height: 28, borderRadius: '50%', flexShrink: 0 };
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: 'absolute', left: 34, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default function DisciplineAbsenceMonitor() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const [mode, setMode] = useState('general'); // 'general' | 'islamic'
  const [yearId, setYearId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [periodId, setPeriodId] = useState('');
  const [classId, setClassId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [gender, setGender] = useState('');
  const [periods, setPeriods] = useState([]);
  const [classes, setClasses] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [islamicClasses, setIslamicClasses] = useState([]);
  const [islamicRooms, setIslamicRooms] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dormModal, setDormModal] = useState(null); // { dorm_id, dorm_name, dorm_code, village_name, st_id }
  const [dormResidents, setDormResidents] = useState([]);
  const [dormLoaded, setDormLoaded] = useState(false);

  useEffect(() => {
    axiosInstance.get('/api/getLastestStudyYear')
      .then(res => setYearId(res.data?.year_id || ''))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearId) return;
    Promise.allSettled([
      axiosInstance.get(`/api/getPeriodList/${yearId}`),
      axiosInstance.get(`/api/getClassListByYear/${yearId}`),
      axiosInstance.get(`/api/getRoomListByYear/${yearId}`),
      axiosInstance.get('/api/islamic/getIslamicClasses'),
      axiosInstance.get(`/api/islamic/getRooms/${yearId}`),
    ]).then(([periodRes, classRes, roomRes, iClassRes, iRoomRes]) => {
      if (periodRes.status === 'fulfilled') setPeriods(periodRes.value.data || []);
      if (classRes.status  === 'fulfilled') setClasses(classRes.value.data || []);
      if (roomRes.status   === 'fulfilled') setRooms(roomRes.value.data || []);
      if (iClassRes.status === 'fulfilled') setIslamicClasses(iClassRes.value.data || []);
      if (iRoomRes.status  === 'fulfilled') setIslamicRooms(iRoomRes.value.data || []);
    });
  }, [yearId]);

  const fetchAbsences = useCallback(() => {
    if (!yearId || !date) return;
    setLoading(true);
    const params = new URLSearchParams({ date, yearId });
    if (periodId) params.set('periodId', periodId);
    if (classId)  params.set('classId', classId);
    if (roomId)   params.set('roomId', roomId);
    if (gender)   params.set('gender', gender);

    const endpoint = mode === 'islamic'
      ? `/api/discipline/islamic-absences?${params}`
      : `/api/discipline/absences?${params}`;

    axiosInstance.get(endpoint)
      .then(res => setRows(res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [yearId, date, periodId, classId, roomId, gender, mode]);

  useEffect(() => {
    fetchAbsences();
  }, [fetchAbsences]);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, { auth: { token: getToken() } });
    socket.on('refreshGeneralAttendance', fetchAbsences);
    socket.on('refreshIslamicAttendance', fetchAbsences);
    return () => socket.disconnect();
  }, [fetchAbsences, mode]);

  const filteredRooms = useMemo(() => {
    if (!classId) return rooms;
    return rooms.filter(r => String(r.c_id) === String(classId));
  }, [rooms, classId]);

  const filteredIslamicRooms = useMemo(() => {
    if (!classId) return islamicRooms;
    return islamicRooms.filter(r => String(r.ic_id) === String(classId));
  }, [islamicRooms, classId]);

  const grouped = useMemo(() => {
    const map = new Map();
    rows.forEach(row => {
      const key = mode === 'islamic'
        ? `${row.period_id}-${row.ic_id}-${row.ir_no}`
        : `${row.period_id}-${row.c_id}-${row.r_no}`;
      if (!map.has(key)) {
        const label = mode === 'islamic'
          ? `คาบ ${row.period_order} · ${row.ic_class}/${row.ir_no}`
          : `คาบ ${row.period_order} · ม.${row.c_id}/${row.r_no}`;
        map.set(key, { label, rows: [] });
      }
      map.get(key).rows.push(row);
    });
    return [...map.values()];
  }, [rows, mode]);

  const openDormModal = (row) => {
    setDormModal({
      dorm_id: row.dorm_id,
      dorm_name: row.dorm_name,
      dorm_code: row.dorm_code,
      village_name: row.village_name,
      st_id: row.st_id,
    });
    setDormResidents([]);
    setDormLoaded(false);
    axiosInstance.get(`/api/getDormResidents/${row.dorm_id}`)
      .then(res => setDormResidents(res.data || []))
      .catch(() => setDormResidents([]))
      .finally(() => setDormLoaded(true));
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setClassId('');
    setRoomId('');
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ฝ่ายปกครอง</li>
              <li className="breadcrumb-item active">ติดตามนักเรียนขาดเรียน</li>
            </ol>
            <h4 className="main-title mb-0">ติดตามนักเรียนที่ไม่เข้าเรียน</h4>
            <p className="text-secondary fs-sm mb-0">
              {mode === 'general' ? 'ภาคสามัญ — อัปเดตตามการเช็คชื่อแบบ realtime' : 'ภาคศาสนา'}
            </p>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <div className="d-flex gap-1">
              <button
                className={`btn btn-sm ${mode === 'general' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleModeChange('general')}
              >สามัญ</button>
              <button
                className={`btn btn-sm ${mode === 'islamic' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleModeChange('islamic')}
              >ศาสนา</button>
            </div>
            <Button variant="outline-primary" size="sm" onClick={fetchAbsences} disabled={loading}>
              <i className="ri-refresh-line me-1"></i>{loading ? 'กำลังโหลด' : 'รีเฟรช'}
            </Button>
          </div>
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            <Row className="g-2">
              <Col md={2}>
                <Form.Label>วันที่</Form.Label>
                <ThaiDateInput value={date} onChange={v => setDate(v)} />
              </Col>
              <Col md={2}>
                <Form.Label>คาบ</Form.Label>
                <Form.Select value={periodId} onChange={e => setPeriodId(e.target.value)}>
                  <option value="">ทุกคาบ</option>
                  {periods.map(p => <option key={p.period_id} value={p.period_id}>คาบ {p.period_order}</option>)}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label>ชั้น</Form.Label>
                <Form.Select value={classId} onChange={e => { setClassId(e.target.value); setRoomId(''); }}>
                  <option value="">ทุกชั้น</option>
                  {mode === 'islamic'
                    ? islamicClasses.map(c => <option key={c.ic_id} value={c.ic_id}>{c.ic_class}</option>)
                    : classes.map(c => <option key={c.c_id} value={c.c_id}>ม.{c.c_id}</option>)
                  }
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>ห้อง</Form.Label>
                <Form.Select value={roomId} onChange={e => setRoomId(e.target.value)}>
                  <option value="">ทุกห้อง</option>
                  {mode === 'islamic'
                    ? filteredIslamicRooms.map(r => <option key={r.ir_id} value={r.ir_id}>{r.ic_class}/{r.ir_no}</option>)
                    : filteredRooms.map(r => <option key={r.r_id} value={r.r_id}>ม.{r.c_id}/{r.r_no} {r.p_code}</option>)
                  }
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label>เพศ</Form.Label>
                <Form.Select value={gender} onChange={e => setGender(e.target.value)}>
                  <option value="">ทั้งหมด</option>
                  <option value="m">ชาย</option>
                  <option value="f">หญิง</option>
                </Form.Select>
              </Col>
              <Col md={1} className="d-flex align-items-end">
                <Badge bg="danger" className="w-100 py-2">{rows.length} คน</Badge>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="card-one">
          <Card.Header>
            <Card.Title as="h6" className="mb-0">
              รายชื่อนักเรียนขาดเรียน — {mode === 'islamic' ? 'ภาคศาสนา' : 'ภาคสามัญ'}
            </Card.Title>
          </Card.Header>
          <Card.Body className="p-0">
            {rows.length === 0 ? (
              <div className="text-center text-secondary py-5">ไม่พบนักเรียนขาดเรียนตามตัวกรองนี้</div>
            ) : (
              <Table responsive hover className="mb-0">
                <thead className="table-secondary">
                  <tr>
                    <th style={{ width: 56 }} className="text-center">#</th>
                    <th style={{ width: 130 }}>เลขประจำตัว</th>
                    <th>ชื่อ-สกุล</th>
                    <th className="text-center">ชั้น/ห้อง</th>
                    {mode === 'general' && <th className="text-center">ชั้นศาสนา</th>}
                    <th className="text-center">คาบ</th>
                    <th>หอพัก</th>
                    <th>วิชา</th>
                    <th>ครูผู้สอน</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped.map(group => (
                    <React.Fragment key={group.label}>
                      <tr className="table-light">
                        <td colSpan={mode === 'general' ? 9 : 8} className="fw-semibold text-secondary">
                          {group.label} <Badge bg="danger" className="ms-2">{group.rows.length}</Badge>
                        </td>
                      </tr>
                      {group.rows.map((row, index) => (
                        <tr key={mode === 'islamic'
                          ? `${row.is_study_id}-${row.period_id}`
                          : `${row.study_id}-${row.period_id}`
                        }>
                          <td className="text-center">{index + 1}</td>
                          <td><code>{row.st_idnumber}</code></td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <StudentAvatar photo={row.st_photo} name={row.st_name} />
                              <span>{row.st_name}</span>
                            </div>
                          </td>
                          <td className="text-center">
                            {mode === 'islamic' ? `${row.ic_class}/${row.ir_no}` : `ม.${row.c_id}/${row.r_no}`}
                          </td>
                          {mode === 'general' && (
                            <td className="text-center">
                              {row.ic_class ? `${row.ic_class}/${row.ir_no}` : <span className="text-secondary">-</span>}
                            </td>
                          )}
                          <td className="text-center">{row.period_order}</td>
                          <td>
                            {row.dorm_name ? (
                              <>
                                <span
                                  className="fw-medium text-primary"
                                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                  title="ดูรายชื่อนักเรียนร่วมหอ"
                                  onClick={() => openDormModal(row)}
                                >
                                  {row.dorm_name}
                                </span>
                                <small className="d-block text-secondary">{row.village_name || row.dorm_code || '-'}</small>
                              </>
                            ) : (
                              <span className="text-secondary">ยังไม่จัดหอ</span>
                            )}
                          </td>
                          <td>{row.sub_subname || '-'}</td>
                          <td>{row.teacher_name || '-'}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
        <Modal show={!!dormModal} onHide={() => setDormModal(null)} centered scrollable size="lg">
          <Modal.Header closeButton>
            <Modal.Title as="h6">
              นักเรียนในหอ {dormModal?.dorm_name}
              {dormModal?.dorm_code && <span className="text-secondary ms-1">({dormModal.dorm_code})</span>}
              {dormModal?.village_name && <span className="text-secondary ms-2">หมู่บ้าน{dormModal.village_name}</span>}
              <Badge bg="secondary" className="ms-2">{dormResidents.length} คน</Badge>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {!dormLoaded ? (
              <div className="text-center py-5 text-secondary">กำลังโหลด...</div>
            ) : dormResidents.length === 0 ? (
              <div className="text-center py-5 text-secondary">ไม่พบนักเรียนในหอนี้</div>
            ) : (
              <Table hover responsive className="mb-0 align-middle">
                <thead className="table-secondary">
                  <tr>
                    <th className="text-center" style={{ width: 60 }}>ที่</th>
                    <th className="text-center" style={{ width: 110 }}>เลขประจำตัว</th>
                    <th>ชื่อ-สกุล</th>
                    <th className="text-center" style={{ width: 110 }}>ชั้นเรียน</th>
                    <th className="text-center" style={{ width: 110 }}>ชั้นศาสนา</th>
                  </tr>
                </thead>
                <tbody>
                  {dormResidents.map((r, index) => (
                    <tr key={r.st_id} className={r.st_id === dormModal?.st_id ? 'table-primary' : ''}>
                      <th scope="row" className="text-center">{index + 1}</th>
                      <td className="text-center">{r.st_idnumber}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <StudentAvatar photo={r.st_photo} name={r.st_name} />
                          <span>{r.st_name}</span>
                        </div>
                      </td>
                      <td className="text-center">
                        {r.c_no && r.r_no ? `ม.${r.c_no}/${r.r_no}` : <span className="text-secondary">-</span>}
                      </td>
                      <td className="text-center">
                        {r.ic_class ? `${r.ic_class}/${r.isl_room_no}` : <span className="text-secondary">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
        </Modal>

        <Footer />
      </div>
    </>
  );
}
